/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useEffect, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Grid, Typography, Divider, Switch, TextField, Button, IconButton, Icon, LinearProgress } from '@material-ui/core';
import { BackdropCircular, Breadcrumb } from 'components';
import { getSurveyQuestionConfigsApi, updateStoreConfigsApi } from 'api';


export default function SurveyQuestion() {
  const dispatch = useDispatch();
  const { storeSurveyQuestionConfigs, loadingUpdateStoreConfigs, loadingStoreSurveyQuestionConfigs } = useSelector((state) => state.store_configs);
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [isSurveyQuestionEnabled, setIsSurveyQuestionEnabled] = useState(false);
  const [surveyQuestionValue, setSurveyQuestionValue] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getSurveyQuestionConfigsApi({dispatch});
  }, []);

  useEffect(() => {
    const surveyAnswersValues = storeSurveyQuestionConfigs.find((config) => config.path === 'surveyquestion/general/surveyvalues')?.value || '{}';
    setSurveyAnswers(JSON.parse(surveyAnswersValues));

    const surveyQuestionConfig = storeSurveyQuestionConfigs.find((config) => config.path === 'surveyquestion/general/enable')?.value;
    setIsSurveyQuestionEnabled(surveyQuestionConfig == 1);

    const storeSurveyQuestionValue = storeSurveyQuestionConfigs.find((config) => config.path === 'surveyquestion/general/surveyquestion')?.value;
    setSurveyQuestionValue(storeSurveyQuestionValue);
  }, [storeSurveyQuestionConfigs]);


  const validateFields = () => {
    let fieldsErrors = {};

    if (!isSurveyQuestionEnabled) {
      return fieldsErrors;
    }

    if (!surveyQuestionValue) {
      fieldsErrors = {...fieldsErrors, surveyQuestionValue: 'Survey question is required'};
    } else if (!Object.keys(surveyAnswers).length) {
      fieldsErrors = {...fieldsErrors, surveyQuestionValue: 'At least one answer is required'};
    } else {
      const { surveyQuestionValue, ...otherErrors } = fieldsErrors;
      fieldsErrors = otherErrors;
    }

    Object.entries(surveyAnswers).map(([key, answer]) => {
      if (!answer.value) {
        fieldsErrors = {...fieldsErrors, [`answer-${key}`]: 'Survey answer is required'};
      } else {
        const { [`answer-${key}`]: editedAnswer, ...otherErrors } = fieldsErrors;
        fieldsErrors = otherErrors;
      }
    });


    setErrors(fieldsErrors);
    return fieldsErrors;
  };

  const handleChangeSurveyAnswers = (event, key) => {
    const {value} = event.target;

    if (!value) {
      setErrors((errors) => ({...errors, [`answer-${key}`]: 'Survey answer is required'}));
    } else {
      setErrors(({[`answer-${key}`]: editedAnswer, ...otherErrors}) => otherErrors);
    }

    setSurveyAnswers((surveyAnswers) => ({...surveyAnswers, [key]: {value}}));
  };

  const handleDeleteSurveyAnswers = (key) => {
    setSurveyAnswers(({ [key]: answerToDelete, ...otherSurveyAnswers}) => otherSurveyAnswers);
    setErrors(({[`answer-${key}`]: editedAnswer, ...otherErrors}) => otherErrors);
  };

  const handleAddSurveyAnswers = () => {
    const key = `_${Date.now()}_${Math.floor(Math.random() * 100) + 1}`;
    setSurveyAnswers((surveyAnswers) => ({...surveyAnswers, [key]: {value: ''}}));
  };

  const handleChangeSurweyQuestionValue = (event) => {
    const {value} = event.target;

    if (!value) {
      setErrors((errors) => ({...errors, surveyQuestionValue: 'Survey question is required'}));
    } else {
      setErrors(({surveyQuestionValue, ...otherErrors}) => otherErrors);
    }

    setSurveyQuestionValue(value);
  };

  const handleSaveSurveyQuestionConfigs = () => {
    const errorsObj = validateFields();

    if (Object.keys(errorsObj).length) {
      return;
    }

    const configs = [
      {
        path: 'surveyquestion/general/enable',
        value: isSurveyQuestionEnabled ? '1' : '0'
      },
      {
        path: 'surveyquestion/general/surveyquestion',
        value: surveyQuestionValue
      },
      {
        path: 'surveyquestion/general/surveyvalues',
        value: JSON.stringify(surveyAnswers)
      }
    ];

    updateStoreConfigsApi({dispatch, configs});
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div>
        <Breadcrumb
          isCapitalize={Boolean(false)}
          routeSegments={[
            { name: 'Settings', path: '/settings#go_live_requirements' },
            { name: 'Store Configurations', path: '/settings#store_configurations' },
            { name: 'Survey Question' }
          ]}
        />
      </div>
      <div className="mt-10 mx-10">
        <Card className="p-8">
          {loadingStoreSurveyQuestionConfigs && <LinearProgress className="m-10" />}
          {!loadingStoreSurveyQuestionConfigs && storeSurveyQuestionConfigs.length === 0 && <div className="m-10">Can&apos;t fetch config data</div>}
          {!loadingStoreSurveyQuestionConfigs && storeSurveyQuestionConfigs.length &&
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <p className="text-gray">In this section you can configure survey question for your store</p>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h4">Survey Question</Typography>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                Enable Survey Question:
              </Grid>
              <Grid item xs={12} sm={9} md={9}>
                <Switch
                  color="primary"
                  name="surveyquestion/general/enable"
                  checked={isSurveyQuestionEnabled}
                  onChange={(event) => setIsSurveyQuestionEnabled(event.target.checked)}
                />
              </Grid>
              {isSurveyQuestionEnabled &&
              <>
                <Grid item xs={12} sm={3} md={3}>
                  Survey question:
                </Grid>
                <Grid item xs={12} sm={9} md={9}>
                  <TextField
                    fullWidth
                    className="max-w-600"
                    variant="outlined"
                    name="surveyquestion/general/surveyquestion"
                    value={surveyQuestionValue}
                    label="Survey Question"
                    onChange={handleChangeSurweyQuestionValue}
                    error={errors.surveyQuestionValue}
                    helperText={errors.surveyQuestionValue}
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={3}>
                  Answers:
                </Grid>
                <Grid item xs={12} sm={9} md={9}>
                  {Object.entries(surveyAnswers).map(([key, answer]) => {
                    return (
                      <Grid item key={`survey-${key}`} xs={12} sm={12} md={12} className="pb-4">
                        <TextField
                          fullWidth
                          className="max-w-600"
                          variant="outlined"
                          name="surveyquestion/general/surveyvalues"
                          value={answer.value}
                          data-key={key}
                          onChange={(event) => handleChangeSurveyAnswers(event, key)}
                          error={errors[`answer-${key}`]}
                          helperText={errors[`answer-${key}`]}
                        />
                        {Object.keys(surveyAnswers).length > 1 &&
                        <IconButton
                          title="Delete"
                          onClick={() => handleDeleteSurveyAnswers(key)}
                        >
                          <Icon className="text-muted">delete</Icon>
                        </IconButton>}
                      </Grid>
                    );
                  })}
                  <Grid item xs={12} sm={12} md={12}>
                    <Button
                      variant="contained"
                      onClick={handleAddSurveyAnswers}
                    >Add
                    </Button>
                  </Grid>
                </Grid>
              </>
              }
            </Grid>
          }
        </Card>
        <div className="flex justify-end mt-4">
          <Button variant="contained" color="primary" className="my-10" onClick={handleSaveSurveyQuestionConfigs}>Save Configurations</Button>
        </div>
        <BackdropCircular loading={loadingUpdateStoreConfigs || loadingStoreSurveyQuestionConfigs} />
      </div>
    </div>
  );
}
